import { useCallback, useEffect, useRef, useState } from 'react';
import {
  POINTER_HOVER_EVENT,
  POINTER_TOGGLE_EVENT,
  type PointerAnchor,
  type PointerHoverDetail,
} from './open-event';
import type { PointerMode, PointerTarget } from './types';

const LEAVE_DELAY_MS = 175;
const SLEEP_MS = 1050;

export function usePointerOpen(errorMessage?: string | null) {
  const [target, setTarget] = useState<PointerTarget | null>(null);
  const errorRef = useRef(errorMessage);
  errorRef.current = errorMessage;
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>();
  const sleepTimer = useRef<ReturnType<typeof setTimeout>>();

  const dismiss = useCallback(() => setTarget(null), []);

  const cancelLeave = useCallback(() => {
    clearTimeout(leaveTimer.current);
    clearTimeout(sleepTimer.current);
    setTarget((current) =>
      current?.sleeping ? { ...current, sleeping: false } : current,
    );
  }, []);

  const scheduleLeave = useCallback(() => {
    clearTimeout(leaveTimer.current);
    clearTimeout(sleepTimer.current);
    leaveTimer.current = setTimeout(() => {
      setTarget((current) =>
        current?.mode === 'peek' ? { ...current, sleeping: true } : current,
      );
      sleepTimer.current = setTimeout(
        () =>
          setTarget((current) => (current?.mode === 'peek' ? null : current)),
        SLEEP_MS,
      );
    }, LEAVE_DELAY_MS);
  }, []);

  const expand = useCallback(
    () =>
      setTarget((current) =>
        current
          ? {
              ...current,
              mode: 'open',
              sleeping: false,
              detail: errorRef.current ?? undefined,
            }
          : current,
      ),
    [],
  );

  const hold = useCallback(
    (hovering: boolean) => (hovering ? cancelLeave() : scheduleLeave()),
    [cancelLeave, scheduleLeave],
  );

  useEffect(() => {
    const create = (
      anchor: PointerAnchor,
      mode: PointerMode,
    ): PointerTarget => ({
      detail: errorRef.current ?? undefined,
      mode,
      ...anchor,
    });

    const onHover = (event: Event) => {
      const { hovering, ...anchor } = (event as CustomEvent<PointerHoverDetail>)
        .detail;
      if (!hovering) {
        scheduleLeave();
        return;
      }
      cancelLeave();
      setTarget((current) => current ?? create(anchor, 'peek'));
    };

    const onToggle = (event: Event) => {
      const anchor = (event as CustomEvent<PointerAnchor>).detail;
      cancelLeave();
      setTarget((current) => {
        if (!current) return create(anchor, 'open');
        return current.mode === 'peek'
          ? { ...current, ...create(anchor, 'open'), sleeping: false }
          : null;
      });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) setTarget(null);
    };

    window.addEventListener(POINTER_HOVER_EVENT, onHover);
    window.addEventListener(POINTER_TOGGLE_EVENT, onToggle);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      clearTimeout(leaveTimer.current);
      clearTimeout(sleepTimer.current);
      window.removeEventListener(POINTER_HOVER_EVENT, onHover);
      window.removeEventListener(POINTER_TOGGLE_EVENT, onToggle);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [cancelLeave, scheduleLeave]);

  return { target, dismiss, expand, hold };
}
